import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  Chips,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
/* The channel codes and their labels, from the contract the API validates
   against and the CHECK in sql/220 mirrors. A local copy here would be a third
   statement of one closed set, and the one that drifts. */
import { SUPPORT_TICKET_CHANNELS } from '@dayon/contracts';
import { api, ApiRequestError } from '../../lib/api.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { TicketAttachments } from './TicketAttachments.tsx';
import { openCountWords, performanceWords } from './scope-words.ts';
import { useSession } from '../../lib/session.tsx';

/**
 * The support desk: a queue on the left, the selected ticket on the right.
 *
 * The queue is WORKED, not browsed, so the server orders it by urgency
 * (escalated first, then priority, then oldest) and the SLA figure on every row
 * is recomputed once a second rather than at fetch time — a countdown that only
 * moves when you reload is a countdown an agent stops trusting.
 *
 * What this screen can and cannot do is decided by the API surface, and that
 * surface is now seven routes: list tickets, read one ticket's thread and its
 * hand-over trail, post a reply, reassign, move the status, read per-agent
 * performance, and — new — LOG A TICKET ON A PLAYER'S BEHALF
 * (POST /api/support/tickets, services/api/src/modules/backoffice/support-intake.ts).
 *
 * This paragraph used to say there was "no assign, escalate, resolve or create
 * endpoint" and that those controls were inert. Three of the four have been
 * true for some time and the fourth is true as of the intake route, so the
 * sentence was describing a screen that no longer existed. What is still NOT
 * here is a player-facing intake: nothing anywhere lets a player raise or read
 * a ticket, so a ticket logged below is a record the desk keeps, not a thread
 * the player can follow — and the dialog says exactly that rather than
 * promising a notification with no sender behind it.
 *
 * Nothing here changes a status locally to look finished.
 */

/* ── Wire shapes ────────────────────────────────────────────────────────────
   Field names are the SQL SELECT list, snake_case, and every count/interval is
   ::text off the driver — hence the Number() conversions rather than arithmetic
   on what looks like a number but is a string. */
export interface TicketRow {
  id: string;
  ref: string;
  subject: string;
  topic: string | null;
  status: string;
  priority: string;
  player: string | null;
  player_ref: string | null;
  /** The player's internal id — what /players/:id is keyed on. */
  player_id: string | null;
  /**
   * WHO RAISED IT, when it was not a player.
   *
   * `partner_kind` is 'AGENT', 'AFFILIATE' or null; `partner`/`partner_ref` are
   * that partner's name and reference. A partner ticket carries no player_id by
   * design — it is the partner asking, not one of their players — so without
   * this the queue drew an em dash for it and an em dash for a ticket whose
   * player is genuinely missing, and an operator could not tell which they were
   * looking at.
   */
  partner_kind: 'AGENT' | 'AFFILIATE' | null;
  partner: string | null;
  partner_ref: string | null;
  brand: string | null;
  assignee: string | null;
  /** The admin id behind `assignee`. Null when the ticket is unassigned. */
  assignee_id: string | null;
  created_at: string;
  first_reply_at: string | null;
  resolved_at: string | null;
  /**
   * How the player reached the desk: IN_APP · EMAIL · MESSENGER · PHONE.
   *
   * NULL on every ticket raised before `support.ticket.channel` existed
   * (sql/220) and on every partner-raised one — a portal ticket has no channel
   * a person chose. The panel renders NULL as "not recorded", never as a
   * channel, which is the difference between a fact and a default.
   */
  channel: string | null;
  /**
   * Seconds to FIRST REPLY, **already halved** if this ticket was escalated.
   *
   * So a queue of hour-long targets can hold a 30-minute one, and the header
   * says "SLA targets vary by ticket" rather than naming one that is not
   * universal. The two columns below are what explain the odd one out.
   */
  sla_target_seconds: string;
  /**
   * When it was FIRST escalated, or null.
   *
   * Escalation halves the target ONCE and never restores it (see
   * `packages/db/sql/730_ticket_escalation.sql`), so this is both the record that
   * it happened and the reason the target is what it is.
   */
  escalated_at: string | null;
  /** The target before the halving. Null exactly when `escalated_at` is. */
  sla_target_seconds_before_escalation: string | null;
  /** Accumulated seconds INCLUDING the currently running segment, at fetch time. */
  sla_elapsed: string;
  /** True when the ticket is Pending player. ADR-0010: crosses the wire as a
      real JSON boolean, not the string 'true'/'false' it used to. ADR-0010
      invariant 2 ("their contracts declare z.boolean()") is deferred here,
      not forgotten: TicketRow is hand-rolled and has no @dayon/contracts
      schema yet — the ADR's own Consequences section names this and the
      other four non-`breaching` columns as decisions for whenever those
      contracts are authored, not obligations on this commit. */
  sla_paused: boolean;
  message_count: string;
}

interface TicketMessage {
  id: string;
  author_kind: 'PLAYER' | 'PARTNER' | 'STAFF';
  author_label: string;
  body: string;
  internal: boolean;
  created_at: string;
}

/**
 * One hand-over of a ticket, from GET /api/support/tickets/:id.
 *
 * `support.ticket_assignment` has been written on every reassignment since the
 * module was built and read by nothing. The ticket itself carries only who
 * holds it NOW; who had it before, who moved it and why survive solely here —
 * and a ticket that has changed hands four times is exactly the ticket a
 * complaint is later raised about.
 */
interface Handover {
  id: string;
  /** Null on the first assignment: the ticket was unassigned, not orphaned. */
  fromAdmin: string | null;
  toAdmin: string;
  changedBy: string | null;
  reasonCode: string | null;
  note: string | null;
  at: string;
}

interface AgentPerformance {
  agent: string;
  handled: string;
  resolved: string;
  avg_first_reply_seconds: string;
  avg_resolution_seconds: string;
}

/**
 * GET /api/support/performance. `restrictedTo` names the brands whose tickets
 * the rows count — null when the reader's access covers every brand, which is
 * also what an answer without the field meant.
 */
interface PerformanceAnswer {
  items: AgentPerformance[];
  restrictedTo: string[] | null;
}

/**
 * One candidate from the player picker, out of GET /api/players.
 *
 * The picker searches the REAL player roster — the same route the Players
 * module lists from, brand-scoped by the session — rather than a hard-coded
 * list. Only the five fields the picker shows are named here; the route returns
 * balances and lifetime figures too, and a ticket form has no business
 * repeating them.
 */
interface PlayerHit {
  id: string;
  ref: string;
  name: string;
  brandName: string;
  status: string;
}

/** An admin a ticket may be handed to, from GET /api/support/assignees. */
interface Assignee {
  id: string;
  display_name: string;
  role: string;
  open_tickets: string;
}

/**
 * The statuses an operator may MOVE a ticket to.
 *
 * ANSWERED is deliberately absent: it is set by posting a public reply and by
 * nothing else, so a control that set it by hand would let a ticket claim the
 * player had been answered with no message on the thread — exactly the state
 * the SLA report exists to catch. The contract's `ticketStatusTarget` is the
 * same four values, so a request this form cannot build is also one the API
 * refuses.
 */
const STATUS_TARGETS: readonly { key: string; label: string; blurb: string }[] = [
  {
    key: 'ESCALATED',
    label: 'Escalated',
    blurb:
      'Moves the ticket to the top of the queue AND halves the target to first reply. The halving happens once: a ticket escalated a second time keeps the target it already has, and moving it back out of Escalated does not restore the original — the faster reply was promised when somebody said it was urgent, and un-escalating does not unmake that. The original target stays on the ticket so the change is legible.',
  },
  {
    key: 'PENDING_PLAYER',
    label: 'Pending player',
    blurb:
      'Pauses the SLA clock: the running segment is folded into the elapsed total and nothing accrues until the ticket moves again. It is the one status that stops the countdown without answering anybody.',
  },
  {
    key: 'RESOLVED',
    label: 'Resolved',
    blurb:
      'Closes the ticket and records when it was closed. Refused on a ticket that has never been replied to — resolving one would stop an SLA clock that was never answered.',
  },
  {
    key: 'OPEN',
    label: 'Open (reopen)',
    blurb:
      'Puts the ticket back in the working queue and restarts the clock. Reopening clears the time it was closed and keeps every reply.',
  },
];

/** Why a ticket moved. Closed, because "why are tickets reopened" is a report. */
const STATUS_REASONS: readonly { key: string; label: string }[] = [
  { key: 'AWAITING_PLAYER_REPLY', label: 'Awaiting player reply' },
  { key: 'NEEDS_SPECIALIST', label: 'Needs a specialist' },
  { key: 'SUSPECTED_FRAUD', label: 'Suspected fraud' },
  { key: 'ISSUE_RESOLVED', label: 'Issue resolved' },
  { key: 'DUPLICATE_TICKET', label: 'Duplicate ticket' },
  { key: 'PLAYER_CAME_BACK', label: 'Player came back' },
];

/** Why a ticket changed hands. The handover is a record, so it has a cause. */
const ASSIGN_REASONS: readonly { key: string; label: string }[] = [
  { key: 'SPECIALIST_REQUIRED', label: 'Specialist required' },
  { key: 'LANGUAGE_MATCH', label: 'Language match' },
  { key: 'WORKLOAD_BALANCE', label: 'Workload balance' },
  { key: 'SHIFT_HANDOVER', label: 'Shift handover' },
  { key: 'CONFLICT_OF_INTEREST', label: 'Conflict of interest' },
  { key: 'ESCALATION_OWNERSHIP', label: 'Escalation ownership' },
];

const STATUS_LABEL: Record<string, string> = {
  OPEN: 'Open',
  ANSWERED: 'Answered',
  PENDING_PLAYER: 'Pending player',
  ESCALATED: 'Escalated',
  RESOLVED: 'Resolved',
};

const PRIORITY_LABEL: Record<string, string> = { HIGH: 'High', NORMAL: 'Normal', LOW: 'Low' };

/** support.ticket.partner_kind, as an operator reads it. */
const PARTNER_KIND_LABEL: Record<'AGENT' | 'AFFILIATE', string> = {
  AGENT: 'Agent',
  AFFILIATE: 'Affiliate',
};

/**
 * The design's chips are Escalated / Open / Pending player / Resolved, but the
 * schema also has ANSWERED and the reply route moves OPEN → ANSWERED on the
 * first public reply. Without a chip for it those tickets would be filterable
 * only by "All", so it is listed rather than hidden.
 */
const STATUS_FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'ESCALATED', label: 'Escalated' },
  { key: 'OPEN', label: 'Open' },
  { key: 'ANSWERED', label: 'Answered' },
  { key: 'PENDING_PLAYER', label: 'Pending player' },
  { key: 'RESOLVED', label: 'Resolved' },
] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number]['key'];

/** Queue chips: Resolved reads as done (filled), Escalated as loud (outline). */
const queueTone = (status: string) =>
  status === 'RESOLVED' ? 'accent' : status === 'ESCALATED' ? 'outline' : 'neutral';

/* ── Durations ─────────────────────────────────────────────────────────────
   "04m 12s", "1h 12m", "48s". Minutes are zero-padded so a column of live
   countdowns does not jitter sideways every time it crosses ten minutes. */
function duration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds));
  if (total >= 3600) {
    const hours = Math.floor(total / 3600);
    return `${hours}h ${String(Math.floor((total % 3600) / 60)).padStart(2, '0')}m`;
  }
  const minutes = Math.floor(total / 60);
  if (minutes === 0) return `${total}s`;
  return `${String(minutes).padStart(2, '0')}m ${String(total % 60).padStart(2, '0')}s`;
}

export interface SlaView {
  /** Compact form for the queue column. */
  queue: string;
  /** Fuller form for the detail strip, which has room to qualify. */
  detail: string;
  /** Under five minutes remain: the only place this screen raises its voice. */
  urgent: boolean;
  breached: boolean;
}

/**
 * The SLA clock.
 *
 * `sla_target_seconds` is a target to FIRST REPLY — the header says so — so the
 * verdict is settled the instant `first_reply_at` exists and there is nothing
 * left to count down. That check has to come before every other branch:
 *
 *   The reply route sets first_reply_at but never clears sla_running_since, so
 *   `sla_elapsed` keeps accruing for the life of the ticket. Counting a replied
 *   ticket against it branded TK-20481 — escalated, answered five minutes after
 *   it was raised, against a one-hour target — as "BREACHED · 3h 30m over
 *   target", and it would have flipped back to "met" the moment somebody
 *   resolved it, with no change to the reply history. The tile above the queue
 *   inherited the same overcount.
 *
 * `driftSeconds` is time elapsed in the BROWSER since the list was fetched —
 * the server already folded the running segment into `sla_elapsed`, so the
 * countdown for a ticket that has NOT been replied to is target − elapsed −
 * drift and needs no second request.
 */
export function slaView(ticket: TicketRow, driftSeconds: number): SlaView {
  const target = Number(ticket.sla_target_seconds);

  if (ticket.first_reply_at) {
    // The prototype printed a flat "met" for every resolved ticket. This does
    // not, because the row carries first_reply_at: seed data already contains a
    // resolved ticket whose sla_elapsed is fifteen hours past target, and "met"
    // over a missed reply is a verdict an operator would quote in a review.
    const firstReply = (Date.parse(ticket.first_reply_at) - Date.parse(ticket.created_at)) / 1000;
    return firstReply <= target
      ? { queue: 'met', detail: `met · first reply in ${duration(firstReply)}`, urgent: false, breached: false }
      : { queue: 'missed', detail: `missed · first reply took ${duration(firstReply)}`, urgent: false, breached: true };
  }

  if (ticket.status === 'RESOLVED') {
    return { queue: 'no reply logged', detail: 'resolved · no first reply logged', urgent: false, breached: false };
  }

  // Pending player pauses the clock: the server leaves sla_running_since NULL,
  // so elapsed stops accumulating and there is nothing to count down.
  if (ticket.sla_paused) {
    return { queue: 'paused', detail: 'paused (pending player)', urgent: false, breached: false };
  }

  const remaining = target - Number(ticket.sla_elapsed) - driftSeconds;
  if (remaining <= 0) {
    return { queue: 'BREACHED', detail: `BREACHED · ${duration(-remaining)} over target`, urgent: false, breached: true };
  }
  return {
    queue: `${duration(remaining)} left`,
    detail: `${duration(remaining)} left`,
    urgent: remaining < 300,
    breached: false,
  };
}

const initials = (name: string) =>
  name
    .split(' ')
    .filter((word) => word.length > 1)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

const clockTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-PH', { hour: '2-digit', minute: '2-digit' });

/** Day and time, for the hand-over trail — a ticket can change hands weeks apart. */
const stamp = (iso: string) =>
  new Date(iso).toLocaleString('en-PH', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * The stored reason code, spoken.
 *
 * Falls back to the raw code rather than hiding it: a code written before this
 * list existed, or by a route this screen does not own, is still evidence and
 * must not vanish from the trail because the console has no word for it.
 */
const assignReasonLabel = (code: string | null): string =>
  code === null
    ? 'no reason code recorded'
    : (ASSIGN_REASONS.find((r) => r.key === code)?.label ?? code);

/* ── The queue table ───────────────────────────────────────────────────────
   The design draws this as selectable cards; DataTable is the same thing with
   a header row above 900px and real cards below it, and it brings the caption,
   keyboard row activation and column labels with it. */
interface QueueRow {
  ticket: TicketRow;
  sla: SlaView;
  selected: boolean;
}

const queueColumns: Column<QueueRow>[] = [
  {
    key: 'ref',
    header: 'Ticket ID',
    render: ({ ticket, selected }) => (
      <>
        <Tag tone={selected ? 'accent' : 'outline'}>{ticket.ref}</Tag>
        {/* Selection is written, not only tinted — the accent alone would be
            colour carrying the meaning. */}
        {selected ? (
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            shown in the detail pane
          </div>
        ) : null}
      </>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    render: ({ ticket }) => (
      <Tag tone={queueTone(ticket.status)}>{STATUS_LABEL[ticket.status] ?? ticket.status}</Tag>
    ),
  },
  {
    key: 'subject',
    header: 'Subject',
    render: ({ ticket }) => (
      <>
        {ticket.subject}
        <div className="text-subtle" style={{ fontSize: '11px' }}>
          {ticket.topic ?? 'No topic'} · {formatCount(Number(ticket.message_count))}{' '}
          {Number(ticket.message_count) === 1 ? 'message' : 'messages'}
        </div>
      </>
    ),
  },
  {
    key: 'player',
    header: 'Raised by',
    secondary: true,
    /*
     * Three origins, drawn as three different things.
     *
     * This column printed `ticket.player ?? '—'` over `player_ref ?? '—'`, so a
     * ticket raised by a partner from their portal — which carries no player by
     * design — and a ticket whose player row is genuinely missing rendered
     * identically. They are opposite facts: one is a partner waiting for an
     * answer, the other is a broken row somebody has to go and look at. The
     * discriminator is `partner_kind`, resolved server-side the same way the
     * Live Ops work queue resolves it.
     */
    render: ({ ticket }) => {
      if (ticket.partner_kind) {
        return (
          <>
            {ticket.partner ?? 'Partner (name not found)'}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {PARTNER_KIND_LABEL[ticket.partner_kind]} · {ticket.partner_ref ?? 'no reference'} ·{' '}
              {ticket.brand ?? '—'}
            </div>
          </>
        );
      }
      if (ticket.player) {
        return (
          <>
            {ticket.player}
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {ticket.player_ref ?? '—'} · {ticket.brand ?? '—'}
            </div>
          </>
        );
      }
      return (
        <>
          <span className="text-subtle">Unattributed</span>
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            no player and no partner on this ticket · {ticket.brand ?? '—'}
          </div>
        </>
      );
    },
  },
  {
    key: 'sla',
    header: 'SLA',
    numeric: true,
    render: ({ sla }) => (
      <span className="tabular" style={sla.urgent ? { color: 'var(--portal-accent-text)' } : undefined}>
        {sla.queue}
      </span>
    ),
  },
];

/* ── The performance drill-down ────────────────────────────────────────────
   Four of the design's six columns exist. A zero from this endpoint means "no
   first reply / no resolution recorded", not "zero seconds", so it is rendered
   as the absence it is. */
const performanceColumns: Column<AgentPerformance>[] = [
  { key: 'agent', header: 'Agent', render: (a) => a.agent },
  {
    key: 'handled',
    header: 'Tickets handled',
    numeric: true,
    render: (a) => <span className="tabular">{formatCount(Number(a.handled))}</span>,
  },
  {
    key: 'resolved',
    header: 'Resolved',
    numeric: true,
    secondary: true,
    render: (a) => <span className="tabular">{formatCount(Number(a.resolved))}</span>,
  },
  {
    key: 'firstReply',
    header: 'Avg first reply',
    numeric: true,
    render: (a) =>
      Number(a.avg_first_reply_seconds) > 0 ? (
        <span className="tabular">{duration(Number(a.avg_first_reply_seconds))}</span>
      ) : (
        <span className="text-subtle">no first reply logged</span>
      ),
  },
  {
    key: 'resolution',
    header: 'Avg resolution',
    numeric: true,
    render: (a) =>
      Number(a.resolved) > 0 && Number(a.avg_resolution_seconds) > 0 ? (
        <span className="tabular">{duration(Number(a.avg_resolution_seconds))}</span>
      ) : (
        <span className="text-subtle">none resolved</span>
      ),
  },
];

/** The note both writes require. Mirrors the contract's `min(10)` exactly. */
const shortNote = (text: string): string | null =>
  text.trim().length < 10
    ? 'Say why — at least 10 characters. A reason code on its own reads as a form number six months later.'
    : null;

export function Support() {
  const { brandId, brands, allowed, session } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the reads that decide
   * what is fetched. An early return covers only the first.
   */
  const canReadPlayers = allowed('players.view');
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  // The prototype was a hash router (#/support/TK-20481). Here react-router owns
  // the path, so only the ticket ref rides in the fragment: /support#TK-20481.
  const [selectedRef, setSelectedRef] = useState<string | null>(() => readTicketHash());

  const [messages, setMessages] = useState<TicketMessage[]>([]);
  /* The hand-over trail comes down the same request as the thread, so it shares
     its generation guard and its loading flag — two flags would let the panel
     claim a ticket has never been reassigned while its own request is still in
     flight, which is the same blank as "never reassigned". */
  const [handovers, setHandovers] = useState<Handover[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [draftError, setDraftError] = useState<string | undefined>(undefined);
  const [noteMode, setNoteMode] = useState(false);
  const [sending, setSending] = useState(false);

  const [performanceOpen, setPerformanceOpen] = useState(false);
  const [performance, setPerformance] = useState<PerformanceAnswer | null>(null);

  /* ── New ticket: the call an operator writes down ─────────────────────────
     Every field here is enabled, and each was disabled for its own stated
     reason until the piece behind it existed: the route
     (POST /api/support/tickets), the column (sql/220's `channel`) and the
     lookup (GET /api/players, which has always been able to search by ref,
     name, phone, email and agent code). */
  const [newTicketOpen, setNewTicketOpen] = useState(false);
  const [intakeQuery, setIntakeQuery] = useState('');
  const [intakeResults, setIntakeResults] = useState<PlayerHit[]>([]);
  const [intakeSearching, setIntakeSearching] = useState(false);
  const [intakePlayer, setIntakePlayer] = useState<PlayerHit | null>(null);
  const [intakeSubject, setIntakeSubject] = useState('');
  const [intakeBody, setIntakeBody] = useState('');
  const [intakeChannel, setIntakeChannel] = useState<string>(SUPPORT_TICKET_CHANNELS[0]!.key);
  const [intakePriority, setIntakePriority] = useState('NORMAL');
  const [intakeErrors, setIntakeErrors] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);

  /* The chosen player stays in the list even after the query moves on. A
     <select> whose value matches no option renders BLANK, and an operator who
     had already picked somebody would read that as the choice being lost. */
  const intakeCandidates = useMemo(
    () =>
      intakePlayer && !intakeResults.some((p) => p.id === intakePlayer.id)
        ? [intakePlayer, ...intakeResults]
        : intakeResults,
    [intakePlayer, intakeResults],
  );

  /* ── Reassignment and status, §15's two write surfaces ────────────────────
     Both gated on `support.respond` — the same WRITE capability that gates
     posting a reply, and deliberately not the `players.view` the queue is read
     with. Handing a ticket to someone, or closing it, is a change the customer
     feels; the Auditor role reads this screen and must not be able to make it. */
  const canRespond = allowed('support.respond');

  const [assignees, setAssignees] = useState<Assignee[]>([]);
  /* Whose tickets each person's open count covers, from the same answer as
     the counts: null when the reader's access covers every brand. */
  const [assigneeScope, setAssigneeScope] = useState<string[] | null>(null);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTo, setAssignTo] = useState('');
  const [assignReason, setAssignReason] = useState(ASSIGN_REASONS[0]!.key);
  const [assignNote, setAssignNote] = useState('');
  const [assignErrors, setAssignErrors] = useState<Record<string, string>>({});
  const [assigning, setAssigning] = useState(false);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusTarget, setStatusTarget] = useState(STATUS_TARGETS[0]!.key);
  const [statusReason, setStatusReason] = useState(STATUS_REASONS[0]!.key);
  const [statusNote, setStatusNote] = useState('');
  const [statusErrors, setStatusErrors] = useState<Record<string, string>>({});
  const [movingStatus, setMovingStatus] = useState(false);

  // One shared tick drives every countdown on the screen, so all of them move
  // together and a hundred rows cost one timer rather than one hundred. Both
  // clocks start at zero and are set from an effect: reading the wall clock
  // while rendering makes the render impure.
  /**
   * WHAT THE OPERATOR IS LOOKING FOR.
   *
   * The queue is the 100 MOST URGENT tickets and stops there — no paging past
   * the cut. Without a search, a ticket outside that hundred could not be
   * reached from this screen at all, and somebody holding a reference a player
   * had just read out was reduced to the browser's own find, which only looks
   * at rows already drawn.
   *
   * SENT TO THE ROUTE, not filtered here, for the same reason: filtering the
   * loaded rows would search the hundred it already has and still miss the one
   * that matters. The route matches the ticket reference, the subject, and the
   * player's name and reference.
   */
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    /* Debounced for the same reason the other queues are: a request per
       keystroke makes the rows flicker between stale responses. */
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const [now, setNow] = useState(0);
  const [fetchedAt, setFetchedAt] = useState(0);
  useEffect(() => {
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // A request generation, so a slow response for the previous brand scope
  // cannot land on top of a newer one.
  const ticketGeneration = useRef(0);
  const loadTickets = useCallback(() => {
    /* NOT ASKED. The module's refusal near the bottom is a RENDER decision and
       runs after every hook here was registered, so this read still went out
       for a role the route refuses. Guarded in the callback rather than only in
       the effect that calls it on mount, so a later edit to the caller cannot
       quietly un-guard it; the buttons that also call it need a painted control
       and were never the risk. */
    if (!canReadPlayers) {
      setLoading(false);
      return Promise.resolve();
    }
    const mine = ++ticketGeneration.current;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    if (debouncedSearch) params.set('q', debouncedSearch);
    const query = params.toString();

    return api
      .get<{ items: TicketRow[] }>(`/api/support/tickets${query ? `?${query}` : ''}`)
      .then((d) => {
        if (mine !== ticketGeneration.current) return;
        setTickets(d.items);
        // The drift baseline is the moment the numbers arrived, not mount, or
        // every refetch would restart the countdown from where it already was.
        const arrived = Date.now();
        setFetchedAt(arrived);
        setNow(arrived);
      })
      .catch((error: unknown) => {
        if (mine !== ticketGeneration.current) return;
        showError({
          title: 'Could not load the ticket queue',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => {
        if (mine === ticketGeneration.current) setLoading(false);
      });
  }, [canReadPlayers, brandId, debouncedSearch, showError]);

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  // Back and forward through deep links must move the selection, not just the URL.
  useEffect(() => {
    const onHash = () => setSelectedRef(readTicketHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const selected = useMemo(
    () => tickets.find((t) => t.ref === selectedRef) ?? null,
    [tickets, selectedRef],
  );
  const selectedId = selected?.id ?? null;

  const messageGeneration = useRef(0);
  const loadMessages = useCallback(
    (ticketId: string) => {
      /* NOT ASKED. The module's refusal near the bottom is a RENDER decision and
         runs after every hook here was registered, so this read still went out
         for a role the route refuses. Guarded in the callback rather than only in
         the effect that calls it on mount, so a later edit to the caller cannot
         quietly un-guard it; the buttons that also call it need a painted control
         and were never the risk. */
      if (!canReadPlayers) {
        setMessagesLoading(false);
        return Promise.resolve();
      }
      const mine = ++messageGeneration.current;
      setMessagesLoading(true);
      return api
        .get<{ messages: TicketMessage[]; handovers: Handover[] }>(
          `/api/support/tickets/${ticketId}`,
        )
        .then((d) => {
          if (mine !== messageGeneration.current) return;
          setMessages(d.messages);
          setHandovers(d.handovers);
        })
        .catch((error: unknown) => {
          if (mine !== messageGeneration.current) return;
          showError({
            title: 'Could not load the conversation',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        })
        .finally(() => {
          if (mine === messageGeneration.current) setMessagesLoading(false);
        });
    },
    [canReadPlayers, showError],
  );

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      setHandovers([]);
      return;
    }
    void loadMessages(selectedId);
  }, [selectedId, loadMessages]);

  // Both clocks are zero until the first effect runs; taking the later of the
  // two keeps the drift at zero rather than measuring from 1970.
  const nowMs = Math.max(now, fetchedAt);
  const drift = fetchedAt === 0 ? 0 : Math.max(0, Math.floor((nowMs - fetchedAt) / 1000));

  const counts = useMemo(() => {
    const byStatus = new Map<string, number>();
    for (const t of tickets) byStatus.set(t.status, (byStatus.get(t.status) ?? 0) + 1);
    return byStatus;
  }, [tickets]);

  const openCount = tickets.filter((t) => t.status !== 'RESOLVED').length;
  const escalated = counts.get('ESCALATED') ?? 0;

  // A live breach: an unresolved ticket that has missed its first-reply target,
  // whether it is still counting down or has since been answered late. There is
  // no sla_breached_at column, so this is derived — and it cannot be scoped to
  // "today", which is why the tile does not claim to be.
  //
  // The paused guard that used to sit here is gone. slaView already returns
  // breached:false for a paused ticket that has not been replied to, so the
  // guard bought nothing — and once a paused ticket carries a LATE first reply
  // the guard actively hid a breach that had already happened.
  const breaching = tickets.filter((t) => t.status !== 'RESOLVED' && slaView(t, drift).breached).length;

  const replied = tickets.filter((t) => t.first_reply_at);
  const avgFirstReply = replied.length
    ? replied.reduce((sum, t) => sum + (Date.parse(t.first_reply_at!) - Date.parse(t.created_at)) / 1000, 0) /
      replied.length
    : null;

  const startOfToday = new Date(nowMs).setHours(0, 0, 0, 0);
  const resolvedToday = tickets.filter(
    (t) => t.resolved_at && Date.parse(t.resolved_at) >= startOfToday,
  ).length;
  const resolvedInScope = tickets.filter((t) => t.resolved_at).length;

  // Every loaded ticket carries the same target in seed data, but nothing
  // guarantees that, so the header states the target only when it is one value.
  const targets = new Set(tickets.map((t) => t.sla_target_seconds));
  const [onlyTarget] = [...targets];
  const targetLine =
    targets.size === 1 && onlyTarget
      ? `SLA target ${duration(Number(onlyTarget))} to first reply`
      : targets.size === 0
        ? 'no tickets in this scope'
        : 'SLA targets vary by ticket';

  const brandName = brands.find((b) => b.id === brandId)?.name ?? null;
  const emptyScope = brandName ?? 'this filter';

  /* Performance's words about whose tickets its rows count, from the answer
     the rows came in. Null until that answer is here, and nothing is said
     about breadth before there are figures to say it about. */
  const performanceText = performance === null ? null : performanceWords(performance.restrictedTo);

  const visible: QueueRow[] = tickets
    .filter((t) => statusFilter === 'ALL' || t.status === statusFilter)
    .map((ticket) => ({ ticket, sla: slaView(ticket, drift), selected: ticket.ref === selectedRef }));

  const chipOptions = STATUS_FILTERS.map((f) => ({
    key: f.key,
    label: f.label,
    count: f.key === 'ALL' ? tickets.length : (counts.get(f.key) ?? 0),
  }));

  function select(ticket: TicketRow) {
    // Re-clicking the ticket already open must not wipe a half-typed reply;
    // only a genuine change of ticket resets the composer.
    if (ticket.ref !== selectedRef) {
      setDraft('');
      setDraftError(undefined);
      setNoteMode(false);
    }
    setSelectedRef(ticket.ref);
    // Written to the fragment so the ticket is linkable into a chat or an email.
    window.location.hash = ticket.ref;
  }

  /* ── The player picker ───────────────────────────────────────────────────
     Debounced, generation-free because a stale response can only overwrite a
     newer one within 250ms of it, and CANCELLED on close so a search that lands
     after the dialog shuts cannot repopulate a list nobody is looking at.

     It carries the brand selector, so the operator searches the brand they are
     working. The API scopes the search to the brands they hold regardless —
     this narrows within that, it does not widen it. */
  useEffect(() => {
    if (!newTicketOpen || !canReadPlayers) return;
    const q = intakeQuery.trim();
    if (q.length < 2) {
      setIntakeResults([]);
      setIntakeSearching(false);
      return;
    }
    let cancelled = false;
    setIntakeSearching(true);
    const timer = setTimeout(() => {
      const params = new URLSearchParams({ q, perPage: '8' });
      if (brandId) params.set('brandId', brandId);
      api
        .get<{ items: PlayerHit[] }>(`/api/players?${params.toString()}`)
        .then((d) => {
          if (!cancelled) setIntakeResults(d.items);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          // Emptied, not left stale: a list under a failed search reads as the
          // answer to the words currently in the box.
          setIntakeResults([]);
          showError({
            title: 'Could not search for a player',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        })
        .finally(() => {
          if (!cancelled) setIntakeSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canReadPlayers, newTicketOpen, intakeQuery, brandId, showError]);

  function openNewTicket() {
    setIntakeQuery('');
    setIntakeResults([]);
    setIntakePlayer(null);
    setIntakeSubject('');
    setIntakeBody('');
    setIntakeChannel(SUPPORT_TICKET_CHANNELS[0]!.key);
    setIntakePriority('NORMAL');
    setIntakeErrors({});
    setNewTicketOpen(true);
  }

  /**
   * Log the call.
   *
   * The three checks below are the API's own, repeated here so the operator is
   * answered beside the field rather than losing a typed-up call to a
   * round-trip. The server is still the guard that counts — and the brand
   * boundary is ONLY the server's: the player is resolved inside the caller's
   * scope there, and nothing this form sends can widen it.
   */
  async function submitNewTicket() {
    const errors: Record<string, string> = {};
    if (!intakePlayer) errors['playerId'] = 'Choose the player this is about.';
    if (intakeSubject.trim().length < 3) errors['subject'] = 'Give the ticket a subject.';
    if (intakeBody.trim().length < 3) errors['body'] = 'Write down what the player reported.';
    if (Object.keys(errors).length > 0) {
      setIntakeErrors(errors);
      return;
    }

    setCreating(true);
    try {
      const made = await api.post<{ ref: string; player: string; playerRef: string }>(
        '/api/support/tickets',
        {
          playerId: intakePlayer!.id,
          subject: intakeSubject.trim(),
          body: intakeBody.trim(),
          channel: intakeChannel,
          priority: intakePriority,
        },
      );
      // Re-read rather than pushing a row in: the queue is ORDERED by urgency
      // on the server, and a ticket inserted client-side would sit wherever the
      // console guessed rather than where the desk will actually find it.
      await loadTickets();
      setSelectedRef(made.ref);
      window.location.hash = made.ref;
      toast(`${made.ref} opened for ${made.player} · SLA clock running`);
      setNewTicketOpen(false);
    } catch (error: unknown) {
      // Field messages stay in the form; everything else is the modal, and the
      // dialog stays OPEN so nothing typed is lost to a failed request.
      if (error instanceof ApiRequestError && error.fields) {
        setIntakeErrors(error.fields);
        return;
      }
      showError({
        title: 'The ticket was not created',
        message:
          error instanceof ApiRequestError
            ? `Nothing was logged. ${error.message}`
            : 'Nothing was logged. Try again shortly.',
      });
    } finally {
      setCreating(false);
    }
  }

  function openPerformance() {
    setPerformanceOpen(true);
    if (performance) return;
    api
      .get<{ items: AgentPerformance[]; restrictedTo?: string[] | null }>(
        '/api/support/performance',
      )
      .then((d) => setPerformance({ items: d.items, restrictedTo: d.restrictedTo ?? null }))
      .catch((error: unknown) =>
        showError({
          title: 'Could not load support performance',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        }),
      );
  }

  async function send() {
    if (!selected) return;
    const text = draft.trim();
    if (!text) {
      // The prototype raised a toast here. In this console the toast channel is
      // successes only, and a field-level refusal belongs beside the field it
      // refuses — a toast can be missed, an inline error cannot.
      setDraftError('Type a message first');
      return;
    }

    setSending(true);
    // Captured before the refetch: only the FIRST public reply stops the
    // first-reply clock, so only that one may claim to have stopped it.
    const wasFirstReply = !selected.first_reply_at;
    const player = selected.player ?? 'the player';
    try {
      const result = await api.post<{
        posted: boolean;
        notice: { messageRef: string; status: string } | null;
        noticeSkipped:
          | 'INTERNAL_NOTE'
          | 'NOT_A_PLAYER_TICKET'
          | 'NO_ADDRESS'
          | 'ADDRESS_NOT_CONFIRMED'
          | null;
      }>(`/api/support/tickets/${selected.id}/reply`, {
        body: text,
        internal: noteMode,
      });
      setDraft('');
      setDraftError(undefined);
      /*
       * WHETHER THE PLAYER WAS ACTUALLY TOLD, said here rather than assumed.
       *
       * A public reply now records an email — but only to an address that is on
       * file and CONFIRMED, and never on a partner ticket. An agent who reads
       * "reply sent" and infers "they have been notified" would stop chasing a
       * player who has no way of knowing an answer exists until they next open
       * the app. The route returns the reason it did not send; this repeats it.
       */
      const delivery =
        result.notice !== null
          ? ` · player emailed (${result.notice.messageRef})`
          : result.noticeSkipped === 'ADDRESS_NOT_CONFIRMED'
            ? ' · NOT emailed — their address is not confirmed'
            : result.noticeSkipped === 'NO_ADDRESS'
              ? ' · NOT emailed — no address on this account'
              : '';
      toast(
        noteMode
          ? 'Internal note saved · staff only, not shown to the player'
          : wasFirstReply
            ? `Reply sent to ${player} · SLA first-reply clock stopped${delivery}`
            : `Reply sent to ${player}${delivery}`,
      );
      // Refetch both: the reply may have set first_reply_at and moved the
      // status, and the thread now has one more bubble.
      void loadTickets();
      void loadMessages(selected.id);
    } catch (error: unknown) {
      showError({
        title: 'The reply was not sent',
        message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
      });
    } finally {
      setSending(false);
    }
  }

  /*
   * Who a ticket may be handed to.
   *
   * Loaded once, and only for a reader holding support.respond — the key the
   * route asks for. The list is only the admins whose role actually holds
   * support.respond, so the picker cannot build a handover the write route is
   * obliged to refuse.
   */
  useEffect(() => {
    if (!canRespond) return;
    let cancelled = false;
    api
      .get<{ items: Assignee[]; restrictedTo?: string[] | null }>('/api/support/assignees')
      .then((d) => {
        if (cancelled) return;
        setAssignees(d.items);
        setAssigneeScope(d.restrictedTo ?? null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        showError({
          title: 'Could not load the assignee list',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [canRespond, showError]);

  function openAssign(preferred?: string) {
    if (!selected) return;
    const mine = session?.id ?? '';
    const fallback =
      assignees.find((a) => a.id !== selected.assignee_id)?.id ?? assignees[0]?.id ?? '';
    setAssignTo(preferred ?? (mine && mine !== selected.assignee_id ? mine : fallback));
    setAssignReason(ASSIGN_REASONS[0]!.key);
    setAssignNote('');
    setAssignErrors({});
    setAssignOpen(true);
  }

  function openStatus(target: string) {
    if (!selected) return;
    setStatusTarget(target);
    setStatusReason(STATUS_REASONS[0]!.key);
    setStatusNote('');
    setStatusErrors({});
    setStatusOpen(true);
  }

  async function submitAssign() {
    if (!selected) return;
    const errors: Record<string, string> = {};
    if (!assignTo) errors['toAdminId'] = 'Choose who is taking the ticket.';
    const note = shortNote(assignNote);
    if (note) errors['note'] = note;
    if (Object.keys(errors).length > 0) {
      setAssignErrors(errors);
      return;
    }

    setAssigning(true);
    try {
      const result = await api.post<{ ref: string; assignee: string; previousAssignee: string | null }>(
        `/api/support/tickets/${selected.id}/assign`,
        { toAdminId: assignTo, reasonCode: assignReason, note: assignNote.trim() },
      );
      // Re-read: the assignee column, the per-agent performance drill-down and
      // the handover count all come off the queue, not off this one row.
      await loadTickets();
      toast(
        result.previousAssignee
          ? `${result.ref} handed from ${result.previousAssignee} to ${result.assignee} · handover recorded`
          : `${result.ref} assigned to ${result.assignee} · handover recorded`,
      );
      setAssignOpen(false);
    } catch (error: unknown) {
      showError({
        title: `Could not reassign ${selected.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The ticket did not change hands. Try again shortly.',
      });
    } finally {
      setAssigning(false);
    }
  }

  async function submitStatus() {
    if (!selected) return;
    const errors: Record<string, string> = {};
    /*
     * The SLA rule, refused here as well as at the API.
     *
     * The server is the guard that counts — this one exists so the operator is
     * told beside the control instead of losing the dialog to a round-trip, and
     * so the console does not ask for something it already knows is refused.
     */
    if (statusTarget === 'RESOLVED' && !selected.first_reply_at) {
      errors['status'] =
        'This ticket has never been replied to. Resolving it would stop an SLA clock that was never answered — post a reply first.';
    }
    const note = shortNote(statusNote);
    if (note) errors['note'] = note;
    if (Object.keys(errors).length > 0) {
      setStatusErrors(errors);
      return;
    }

    setMovingStatus(true);
    try {
      const result = await api.post<{
        ref: string;
        status: string;
        slaStopped: boolean;
        slaTargetSeconds: number;
        slaTargetHalved: boolean;
        previouslyEscalated: boolean;
      }>(`/api/support/tickets/${selected.id}/status`, {
        status: statusTarget,
        reasonCode: statusReason,
        note: statusNote.trim(),
      });
      // A status move changes the queue ORDER, the chip badges and three of the
      // four tiles above it, so the list is re-read rather than the row patched.
      await loadTickets();
      const label = STATUS_LABEL[result.status] ?? result.status;
      toast(
        result.status === 'RESOLVED'
          ? `${result.ref} resolved · SLA clock stopped`
          : result.slaTargetHalved
            ? `${result.ref} escalated · target halved to ${duration(result.slaTargetSeconds)} to first reply`
            : /* Said out loud, because the operator pressed Escalated and the
                 target did not move. The status DID change; the halving had
                 already been spent. */
              result.status === 'ESCALATED' && result.previouslyEscalated
              ? `${result.ref} escalated · target stays at ${duration(result.slaTargetSeconds)}, already halved when it was first escalated`
              : result.slaStopped
                ? `${result.ref} moved to ${label} · SLA clock paused`
                : `${result.ref} moved to ${label} · SLA clock running`,
      );
      setStatusOpen(false);
    } catch (error: unknown) {
      showError({
        title: `Could not move ${selected.ref}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The ticket did not move. Try again shortly.',
      });
    } finally {
      setMovingStatus(false);
    }
  }

  if (!canReadPlayers) {
    return (
      <EmptyState icon="lock-key" title="Support">
        This module needs the players.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  const selectedSla = selected ? slaView(selected, drift) : null;

  return (
    <>
      <ScreenHeader
        /* "Support management", not the sidebar's shorter "Support", and that is
           deliberate rather than an oversight. The product IA names this module
           "Support management", the design mock titles the screen that way, and
           three browser walks find this header by that exact name; renaming it
           here would rename the thing those three agree on to save one word.
           The capability refusal above is still titled "Support" — it is the
           sidebar entry answering back to somebody who cannot open the module,
           which is the one place the shorter name is the right one. */
        title="Support management"
        /* The sentence the module comment above has always carried, now where
           the agent can read it. The order in it is the order the server sorts
           the queue in — escalated, then priority, then oldest — so "most urgent
           first" is a description of what arrives, not an instruction nobody
           could follow. */
        purpose="The support desk's working queue: agents take the most urgent ticket first and answer it before its reply deadline passes."
        /*
          The caption moves across unchanged, both halves of it.

          "Loading…" is held for the FIRST read only — `loading && tickets.length
          === 0` — because a refetch behind a filter change still has the
          previous answer on screen, and blanking a figure an agent is reading to
          say "working on it" is worse than letting it stand for the half second
          it takes to be replaced.

          `targetLine` is three-state for its own reason, and the three are not
          interchangeable: one shared target is stated outright, a queue of
          mixed targets says it varies rather than quoting one of them as though
          it applied to all, and an empty queue says there is nothing in scope
          instead of quoting a target no ticket has.

          The brand half always renders, so the scope is never implicit: " · all
          brands" is a claim about breadth that has to be as visible as a single
          brand's name, or an agent reads a three-brand queue as their own.

          What this line does NOT have is a failed-read state, and that is worth
          saying plainly rather than leaving somebody to find it: when the queue
          read fails, the failure is raised in a dialog the agent has to dismiss,
          and this caption falls to "0 open · no tickets in this scope". That is
          the one wording here that can be wrong. It is not fixed in passing with
          this header, because the fix is a new piece of state threaded through
          the tiles, the chip badges and the table's own empty state so that all
          four agree — the same shape `Players` and `Points Mall` carry — and
          doing half of it would leave two of them contradicting each other.
        */
        meta={
          <>
            {loading && tickets.length === 0
              ? 'Loading…'
              : `${formatCount(openCount)} open · ${targetLine}`}
            {brandName ? ` · ${brandName}` : ' · all brands'}
          </>
        }
        actions={
          <>
            <Button onClick={openPerformance}>
              <Icon name="chart-line" />
              Performance
            </Button>
            {/* Gated on the capability the route itself holds, and DISABLED with
                its true reason rather than hidden: an Auditor reads this queue,
                and a button that opened a form the platform would refuse is worse
                than one that says why it cannot. */}
            <Button
              variant="primary"
              onClick={openNewTicket}
              disabled={!canRespond}
              title={
                canRespond
                  ? undefined
                  : 'Logging a ticket is not something your role can do. Ask a Super Admin if you need it.'
              }
            >
              <Icon name="plus" />
              New ticket
            </Button>
          </>
        }
      />

      <div className="stat-grid">
        <Stat
          label="Open tickets"
          value={loading && tickets.length === 0 ? '—' : formatCount(openCount)}
          delta={`${formatCount(escalated)} escalated`}
          deltaUp={escalated > 0}
        />
        <Stat
          label="Avg first reply"
          value={avgFirstReply === null ? '—' : duration(avgFirstReply)}
          delta={
            avgFirstReply === null
              ? 'no ticket in scope has a first reply yet'
              : `${formatCount(replied.length)} of ${formatCount(tickets.length)} loaded tickets`
          }
        />
        <Stat
          label="SLA breaches"
          value={loading && tickets.length === 0 ? '—' : formatCount(breaching)}
          delta="open tickets past target · not scoped to today"
          deltaUp={breaching > 0}
        />
        <Stat
          label="Resolved today"
          value={loading && tickets.length === 0 ? '—' : formatCount(resolvedToday)}
          delta={`${formatCount(resolvedInScope)} resolved in scope · CSAT not recorded`}
        />
      </div>

      {/* Rule 7 in one place: what these tiles are, and what the design asked for
          that the platform cannot answer. The tiles are computed in this screen
          from GET /api/support/tickets (LIMIT 100), not read from a reporting
          rollup; there is no CSAT survey table, and no sla_breached_at column, so
          a breach is counted live and cannot be scoped to a past day. Said below
          in the operator's words. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          These figures are worked out from the tickets this queue shows — the 100 most urgent for
          the current brand scope, oldest SLA first — and not read from a daily summary. Two
          figures are not shown because nothing records them: player satisfaction is not surveyed,
          so there is no satisfaction score; and the moment a ticket went past its target is not
          kept, so a breach can be counted while it is live but not looked up for a past day, which
          is why SLA breaches is not scoped to today. A third used to be missing too, the channel —
          a ticket does record one now and <strong>New ticket</strong> records it, but only for a
          ticket logged here, so it is shown on a ticket that carries one and read as “not
          recorded” on the rest rather than counted as though every ticket had a channel.
        </div>
      </div>

      <Chips
        options={chipOptions}
        active={statusFilter}
        onChange={setStatusFilter}
        label="Ticket status"
      />

      <div className="split">
        <Panel
          title="Ticket queue"
          actions={
            <span className="text-subtle" style={{ fontSize: '11px' }}>
              oldest SLA first · select a ticket
            </span>
          }
        >
          {/*
            * SEARCHES THE WHOLE QUEUE, not the rows on screen. The table below
            * is the 100 most urgent and there is no paging past that, so a box
            * that filtered what was already drawn would quietly fail on exactly
            * the ticket somebody came here to find.
            */}
          <div className="search-field" style={{ marginBottom: 'var(--space-3)' }}>
            <Icon name="magnifying-glass" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ticket ID, subject, player or player ID"
              aria-label="Search tickets by ID, subject, player name or player ID"
            />
          </div>
          {search.trim() !== '' && (
            <p className="text-subtle" style={{ fontSize: '11px', marginBottom: 'var(--space-2)' }}>
              {loading
                ? 'Searching every ticket in scope…'
                : `${formatCount(tickets.length)} ticket${tickets.length === 1 ? '' : 's'} match “${search.trim()}” — searched across every ticket in your brand scope, not only the ones listed before you typed.`}
            </p>
          )}
          {loading && tickets.length === 0 ? (
            <div className="skeleton" style={{ height: '240px' }}>
              &nbsp;
            </div>
          ) : (
            <DataTable
              caption="Support tickets in the current brand scope, most urgent first, with a live SLA countdown"
              columns={queueColumns}
              rows={visible}
              rowKey={(r) => r.ticket.id}
              onRowClick={(r) => select(r.ticket)}
              empty={
                <EmptyState icon="lifebuoy" title={`No tickets for ${emptyScope}`}>
                  {statusFilter === 'ALL'
                    ? 'Nothing in this brand scope needs support attention.'
                    : `No ticket in this brand scope is ${(
                        STATUS_LABEL[statusFilter] ?? statusFilter
                      ).toLowerCase()}. Switch to All to see the rest of the queue.`}
                </EmptyState>
              }
            />
          )}
        </Panel>

        <div className="stack">
          {!selected ? (
            <EmptyState icon="hand-pointing" title="Select a ticket">
              {selectedRef
                ? `${selectedRef} is not in the current brand scope, or it is not in the 100 most urgent tickets. Switch the brand filter at the top of the console to bring it back.`
                : 'Pick a ticket from the queue to read the conversation, check the SLA clock and reply. The ticket you open is written to the address bar, so the link can be pasted to a colleague.'}
            </EmptyState>
          ) : (
            <>
              <Panel>
                <div className="row">
                  <span
                    aria-hidden="true"
                    style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      background: 'var(--color-tag-accent-bg)',
                      color: 'var(--color-tag-accent-fg)',
                      fontSize: '12px',
                      flex: 'none',
                    }}
                  >
                    {initials(selected.player ?? selected.ref)}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <h4 style={{ margin: 0, fontSize: '15px' }}>
                      {selected.subject}{' '}
                      <Tag tone={selected.status === 'RESOLVED' ? 'accent' : 'outline'}>
                        {STATUS_LABEL[selected.status] ?? selected.status}
                      </Tag>
                    </h4>
                    <div className="text-subtle" style={{ fontSize: '11.5px' }}>
                      {selected.ref} · {selected.player ?? 'Unknown player'} ·{' '}
                      {selected.brand ?? 'No brand'}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
                    gap: 'var(--space-3)',
                    paddingTop: 'var(--space-4)',
                  }}
                >
                  <div>
                    <div className="text-subtle" style={{ fontSize: '11px' }}>
                      SLA
                    </div>
                    <div
                      className="tabular"
                      style={selectedSla?.urgent ? { color: 'var(--portal-accent-text)' } : undefined}
                    >
                      {selectedSla?.detail}
                    </div>
                  </div>
                  <div>
                    <div className="text-subtle" style={{ fontSize: '11px' }}>
                      Priority
                    </div>
                    <div>{PRIORITY_LABEL[selected.priority] ?? selected.priority}</div>
                  </div>
                  <div>
                    <div className="text-subtle" style={{ fontSize: '11px' }}>
                      Assignee
                    </div>
                    <div>{selected.assignee ?? 'Unassigned'}</div>
                  </div>
                </div>

                {/* The maker actions, wired. Reassignment writes a handover row
                    and the status move carries the SLA clock with it; both are
                    gated on support.respond, and nothing here flips a status
                    locally — the queue is re-read after every write. */}
                {canRespond ? (
                  <>
                    <div className="row" style={{ paddingTop: 'var(--space-4)' }}>
                      <Button
                        onClick={() => openAssign(session?.id)}
                        disabled={!session?.id || selected.assignee_id === session.id}
                      >
                        <Icon name="user" />
                        Assign to me
                      </Button>
                      <Button onClick={() => openAssign()}>
                        <Icon name="arrows-left-right" />
                        Reassign
                      </Button>
                      <Button
                        onClick={() => openStatus('ESCALATED')}
                        disabled={selected.status === 'ESCALATED'}
                      >
                        <Icon name="warning-octagon" />
                        Escalate
                      </Button>
                      <Button
                        variant="primary"
                        onClick={() => openStatus('RESOLVED')}
                        disabled={selected.status === 'RESOLVED'}
                      >
                        <Icon name="check-circle" />
                        Resolve
                      </Button>
                    </div>
                    <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                      <Icon name="info" />
                      <div>
                        {/* REWRITTEN TWICE, and the second pass undid half of
                            the first.
                            `c629e2af` fixed real broken English — "One thing the
                            design asks for still" dangled with its ending
                            deleted and ran into the next fragment with no space,
                            so the screen read "…asks for stillEscalating halves
                            the target…". That fix stands.
                            It also stripped `resolved_at` AND `support.respond`,
                            on the reasoning that the reader can grant neither.
                            Only the first belongs in that sentence: a column
                            name is plumbing, but a permission key is on the
                            permission matrix in this very console, and "ask for
                            support.respond" is something an operator can
                            actually go and do. `4b0387b5` settled that for
                            fifteen screens an hour later and every other module
                            names its keys — this notice was the one place in the
                            console that stopped, which made Support the outlier
                            rather than the policy. The key is back; the column
                            name is not. */}
                        <strong>Reassigning</strong> records who it moved to and when — the
                        handover is the record, not just a new name on the ticket.{' '}
                        <strong>Moving it</strong> carries the reply clock with it:{' '}
                        <strong>Pending player</strong> pauses the clock,{' '}
                        <strong>Resolved</strong> stops it and stamps the time. Resolving a ticket
                        nobody has replied to is refused — that would stop a clock counting an
                        answer that never came. Either way the change and its record are written
                        together, so one cannot happen without the other — all three gated on{' '}
                        <code>support.respond</code>.{' '}
                        <strong>Escalating halves the target to first reply</strong>, once and for
                        good: escalating a second time keeps the target it already has, and moving
                        the ticket back out does not restore the original. The target it started
                        with stays on the ticket, so a 30-minute target sitting beside a queue of
                        hour-long ones explains itself.{' '}
                        <strong>New ticket</strong>, above, logs one on a player&rsquo;s behalf,
                        behind that same permission.
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="notice notice-quiet" style={{ marginTop: 'var(--space-4)' }}>
                    <Icon name="lock-simple" />
                    <div>
                      Replying, reassigning and moving a ticket all need{' '}
                      <code>support.respond</code>, which your role does not hold. You can read the
                      queue and the conversation, but a change a player would see is not yours to
                      make, so those controls are not offered. Ask a Super Admin if you need it.
                    </div>
                  </div>
                )}
              </Panel>

              <Panel
                title="Conversation"
                actions={
                  <span className="text-subtle" style={{ fontSize: '11px' }}>
                    the player sees replies · never internal notes
                  </span>
                }
              >
                {messagesLoading && messages.length === 0 ? (
                  <div className="skeleton" style={{ height: '160px' }}>
                    &nbsp;
                  </div>
                ) : messages.length === 0 ? (
                  <EmptyState icon="chat-dots" title="No messages yet">
                    This ticket has no messages on the thread. The first reply below starts the
                    player-facing conversation and stops the first-reply clock.
                  </EmptyState>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-3)',
                      maxHeight: '360px',
                      overflowY: 'auto',
                    }}
                  >
                    {messages.map((m) => {
                      const staff = m.author_kind !== 'PLAYER';
                      return (
                        <div
                          key={m.id}
                          style={{
                            alignSelf: staff ? 'flex-end' : 'flex-start',
                            maxWidth: '88%',
                            padding: 'var(--space-3) var(--space-4)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '13px',
                            // An internal note is deliberately NOT accent-tinted:
                            // it must never read like something the player saw.
                            background: m.internal
                              ? 'color-mix(in srgb, var(--color-text) 8%, transparent)'
                              : staff
                                ? 'var(--portal-accent-tint)'
                                : 'var(--color-bg)',
                            boxShadow: m.internal ? 'inset 0 0 0 1px var(--color-divider)' : 'none',
                          }}
                        >
                          <div className="text-subtle" style={{ fontSize: '11px' }}>
                            {m.internal ? `Internal note · ${m.author_label}` : m.author_label} ·{' '}
                            {clockTime(m.created_at)}
                          </div>
                          <div>{m.body}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* THE EVIDENCE, between the thread and the reply box, because
                    that is the order the work happens in: read what they said,
                    look at what they sent, then answer. */}
                <div style={{ paddingTop: 'var(--space-4)' }}>
                  <TicketAttachments
                    ticketId={selected.id}
                    /* A null session cannot respond. Defaulting to TRUE would
                       show an enabled control that the server then refuses,
                       which is the disabled-control honesty rule inverted. */
                    canRespond={session?.permissions.includes('support.respond') ?? false}
                  />
                </div>

                <div className="stack" style={{ paddingTop: 'var(--space-4)' }}>
                  <Field
                    label={noteMode ? 'Internal note — staff only' : 'Reply to player'}
                    error={draftError}
                    hint={
                      noteMode
                        ? 'Saved against the ticket for staff. It never surfaces in the player-facing thread and does not stop the first-reply clock.'
                        : undefined
                    }
                  >
                    {({ id, describedBy, invalid }) => (
                      <Input
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={invalid || undefined}
                        value={draft}
                        placeholder={noteMode ? 'Internal note…' : 'Reply to player…'}
                        onChange={(e) => {
                          setDraft(e.target.value);
                          if (draftError) setDraftError(undefined);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void send();
                          }
                        }}
                      />
                    )}
                  </Field>
                  <div className="row">
                    <Button
                      variant={noteMode ? 'primary' : 'secondary'}
                      aria-pressed={noteMode}
                      onClick={() => setNoteMode((v) => !v)}
                    >
                      <Icon name={noteMode ? 'lock-simple' : 'note'} />
                      Internal note{noteMode ? ' · on' : ''}
                    </Button>
                    <Button
                      variant="primary"
                      className="header-spacer"
                      busy={sending}
                      onClick={() => void send()}
                    >
                      <Icon name="paper-plane-tilt" />
                      Send
                    </Button>
                  </div>
                </div>
              </Panel>

              {/* ── Hand-over history ───────────────────────────────────────
                  `support.ticket_assignment` records every move of this ticket
                  — from, to, who moved it, the reason code and the note — and
                  until now nothing read it. The ticket row carries only who
                  holds it at this moment, so "who had this before, and why did
                  it move" was written down where nobody could see it. This is
                  the panel that answers it.

                  An ordered list because the trail is chronological and a screen
                  reader should say so; the markers are dropped because each row
                  is already stamped with when it happened. */}
              <Panel title="Hand-over history">
                {messagesLoading && handovers.length === 0 ? (
                  <div className="skeleton" style={{ height: '72px' }}>
                    &nbsp;
                  </div>
                ) : handovers.length === 0 ? (
                  <EmptyState icon="arrows-left-right" title="Never reassigned">
                    This ticket has stayed with whoever first picked it up.{' '}
                    {selected.assignee
                      ? `It is with ${selected.assignee} now.`
                      : 'It is unassigned.'}{' '}
                    A row appears here the first time it changes hands.
                  </EmptyState>
                ) : (
                  <ol className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {handovers.map((h) => (
                      <li key={h.id}>
                        <div className="row">
                          <strong>
                            {h.fromAdmin ?? 'Unassigned'} &rarr; {h.toAdmin}
                          </strong>
                          <span
                            className="text-subtle tabular header-spacer"
                            style={{ fontSize: '11px' }}
                          >
                            {stamp(h.at)}
                          </span>
                        </div>
                        <div className="text-subtle" style={{ fontSize: '11px' }}>
                          {assignReasonLabel(h.reasonCode)} · moved by{' '}
                          {h.changedBy ?? 'an admin no longer on the roster'}
                        </div>
                        {h.note ? <div>{h.note}</div> : null}
                      </li>
                    ))}
                  </ol>
                )}
                <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                  <Icon name="info" />
                  <div>
                    {formatCount(handovers.length)} hand
                    {handovers.length === 1 ? 'over' : 'overs'} recorded. Each row is written at the
                    same moment as the reassignment it describes, so this list and the
                    ticket&rsquo;s current owner cannot disagree. The ticket itself stores only who
                    holds it now.
                  </div>
                </div>
              </Panel>

              <Panel title={selected.partner_kind ? 'Ticket context' : 'Player context'}>
                <dl className="stack">
                  {/* Who raised it, named for what they are. A partner ticket
                      has no player by design, and printing an em dash against
                      "Player" for it says the player is missing when nobody was
                      ever meant to be there. */}
                  {selected.partner_kind ? (
                    <>
                      <div className="def-row">
                        <dt>Raised by</dt>
                        <dd>
                          {selected.partner ?? 'Partner (name not found)'}{' '}
                          <Tag tone="outline">{PARTNER_KIND_LABEL[selected.partner_kind]}</Tag>
                        </dd>
                      </div>
                      <div className="def-row">
                        <dt>Partner reference</dt>
                        <dd className="tabular">{selected.partner_ref ?? 'none recorded'}</dd>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="def-row">
                        <dt>Player</dt>
                        <dd>
                          {selected.player ?? (
                            <span className="text-subtle">
                              unattributed — no player and no partner on this ticket
                            </span>
                          )}
                        </dd>
                      </div>
                      <div className="def-row">
                        <dt>Player reference</dt>
                        <dd className="tabular">{selected.player_ref ?? '—'}</dd>
                      </div>
                    </>
                  )}
                  <div className="def-row">
                    <dt>Brand</dt>
                    <dd>{selected.brand ?? '—'}</dd>
                  </div>
                  <div className="def-row">
                    <dt>Topic</dt>
                    <dd>{selected.topic ?? '—'}</dd>
                  </div>
                  <div className="def-row">
                    <dt>Opened</dt>
                    <dd>{new Date(selected.created_at).toLocaleString('en-PH')}</dd>
                  </div>
                  <div className="def-row">
                    <dt>First reply</dt>
                    <dd>
                      {selected.first_reply_at ? clockTime(selected.first_reply_at) : 'not yet'}
                    </dd>
                  </div>
                  {/* Was "not stored", which stopped being true when sql/220
                      added the column and New ticket started writing it. NULL
                      is still the common case — every ticket raised before the
                      column existed, and every partner-raised one — and it is
                      rendered as what it is rather than defaulted to a channel
                      nobody chose. */}
                  <div className="def-row">
                    <dt>Channel</dt>
                    <dd className={selected.channel ? undefined : 'text-subtle'}>
                      {selected.channel
                        ? (SUPPORT_TICKET_CHANNELS.find((c) => c.key === selected.channel)?.label ??
                          selected.channel)
                        : 'not recorded'}
                    </dd>
                  </div>
                  {/* The target, and WHY it is what it is.
                      A queue of hour-long targets holding one 30-minute ticket is
                      a figure an operator will otherwise read as a mistake. This
                      row is always present, because the target always applies;
                      the escalation half appears only when it happened. */}
                  <div className="def-row">
                    <dt>Target to first reply</dt>
                    <dd>
                      <span className="tabular">
                        {duration(Number(selected.sla_target_seconds))}
                      </span>
                      {selected.escalated_at && selected.sla_target_seconds_before_escalation ? (
                        <div className="text-subtle" style={{ fontSize: '11px' }}>
                          halved from{' '}
                          {duration(Number(selected.sla_target_seconds_before_escalation))} when this
                          was escalated on {clockTime(selected.escalated_at)} — it is not restored if
                          the ticket moves out of Escalated
                        </div>
                      ) : null}
                    </dd>
                  </div>
                </dl>

                {/* The design's context card is last deposit, wallet balance, open
                    risk alerts and KYC state. None of those are on this response,
                    so they stay absent rather than invented — four made-up rows
                    here would be four numbers an agent quotes to a player. What
                    HAS changed is the reason: the response now returns
                    `player_id`, so the profile is one click away and the figures
                    are read where they are authoritative instead of copied. */}
                <div className="notice notice-quiet" style={{ marginTop: 'var(--space-3)' }}>
                  <Icon name="info" />
                  <div>
                    Last deposit, wallet balance, open risk alerts and KYC state are not on the
                    ticket response, so they are absent here rather than estimated. The ticket does
                    now carry the player&rsquo;s id, so{' '}
                    <strong>Open player</strong> below goes straight to the profile that holds all
                    four — nobody has to copy a reference between modules to read them.
                  </div>
                </div>

                <div
                  style={{
                    borderTop: '1px solid var(--color-divider)',
                    marginTop: 'var(--space-4)',
                    paddingTop: 'var(--space-4)',
                  }}
                >
                  {/* Straight to the profile.
                      This navigated to `/players?q=<ref>` — a SEARCH for the
                      player the screen was already looking at — because the
                      ticket response returned only the display reference and
                      /players/:id is keyed on the player's internal id. The
                      response now returns `player_id`, so the search is no
                      longer the best link available; it stays only as the
                      fallback for a ticket that carries a reference and no id,
                      which is a broken row rather than a normal one. */}
                  <Button
                    block
                    disabled={!selected.player_id && !selected.player_ref}
                    onClick={() =>
                      navigate(
                        selected.player_id
                          ? `/players/${encodeURIComponent(selected.player_id)}`
                          : `/players?q=${encodeURIComponent(selected.player_ref ?? '')}`,
                      )
                    }
                  >
                    <Icon name="user" />
                    Open player
                  </Button>
                  <div className="text-subtle" style={{ fontSize: '11px', paddingTop: 'var(--space-2)' }}>
                    {selected.player_id
                      ? `Opens ${selected.player ?? 'the player'}'s profile directly — the ticket carries the player's id, so there is nothing to search for and nothing to copy.`
                      : selected.player_ref
                        ? `This ticket carries the reference ${selected.player_ref} but no player id, which means the player row behind it could not be joined. This opens the player list searched for that reference so somebody can find out why.`
                        : selected.partner_kind
                          ? `${PARTNER_KIND_LABEL[selected.partner_kind]} tickets are raised by the partner rather than by one of their players, so there is no player account to open.`
                          : 'This ticket names no player and no partner, so there is no account to open.'}
                  </div>
                </div>
              </Panel>
            </>
          )}
        </div>
      </div>

      {/* ── Reassign ──────────────────────────────────────────────────────── */}
      <Dialog
        open={assignOpen && selected !== null}
        onClose={() => setAssignOpen(false)}
        title={`Reassign ${selected?.ref ?? 'ticket'}`}
        actions={
          <>
            <Button onClick={() => setAssignOpen(false)} disabled={assigning}>
              Cancel
            </Button>
            <Button variant="primary" busy={assigning} onClick={() => void submitAssign()}>
              Hand over
            </Button>
          </>
        }
      >
        <div className="stack">
          <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
            {selected?.assignee
              ? `${selected.assignee} holds this ticket now.`
              : 'Nobody holds this ticket.'}{' '}
            The handover is written as its own row, with who moved it, when and why — so a ticket
            that has changed hands four times can still say so.
          </p>

          <Field
            label="Assign to"
            error={assignErrors['toAdminId']}
            hint={[
              'Only people who are able to reply. Handing a ticket to somebody who cannot answer it is the same silence with a name on it.',
              openCountWords(assigneeScope),
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                value={assignTo}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                onChange={(e) => {
                  setAssignTo(e.target.value);
                  setAssignErrors({});
                }}
              >
                <option value="">Choose an admin…</option>
                {assignees
                  .filter((a) => a.id !== selected?.assignee_id)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name} · {a.role} · {formatCount(Number(a.open_tickets))} open
                    </option>
                  ))}
              </Select>
            )}
          </Field>

          <Field label="Reason" hint="A closed set, so a handover report can be grouped.">
            {({ id }) => (
              <Select
                id={id}
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
              >
                {ASSIGN_REASONS.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            error={assignErrors['note']}
            hint="Saved on the handover row and copied into the audit log with your name."
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={assignNote}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => {
                  setAssignNote(e.target.value);
                  setAssignErrors({});
                }}
                placeholder="What the next agent needs to know before they open it"
              />
            )}
          </Field>
        </div>
      </Dialog>

      {/* ── Status ────────────────────────────────────────────────────────── */}
      <Dialog
        open={statusOpen && selected !== null}
        onClose={() => setStatusOpen(false)}
        title={`Move ${selected?.ref ?? 'ticket'}`}
        actions={
          <>
            <Button onClick={() => setStatusOpen(false)} disabled={movingStatus}>
              Cancel
            </Button>
            <Button variant="primary" busy={movingStatus} onClick={() => void submitStatus()}>
              {STATUS_TARGETS.find((t) => t.key === statusTarget)?.key === 'RESOLVED'
                ? 'Resolve ticket'
                : 'Move ticket'}
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field
            label="Move to"
            error={statusErrors['status']}
            hint="Answered is missing on purpose: it is set by posting a public reply and by nothing else."
          >
            {({ id, describedBy, invalid }) => (
              <Select
                id={id}
                value={statusTarget}
                aria-describedby={describedBy}
                aria-invalid={invalid || undefined}
                onChange={(e) => {
                  setStatusTarget(e.target.value);
                  setStatusErrors({});
                }}
              >
                {STATUS_TARGETS.filter((t) => t.key !== selected?.status).map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <p className="text-muted" style={{ margin: 0, fontSize: '13px' }}>
            {STATUS_TARGETS.find((t) => t.key === statusTarget)?.blurb}
          </p>

          <Field label="Reason" hint="A fixed list, so reopened tickets can be counted by reason rather than read one by one.">
            {({ id }) => (
              <Select
                id={id}
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
              >
                {STATUS_REASONS.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Note"
            error={statusErrors['note']}
            hint="Kept in the audit trail along with where the ticket moved from and to. Nothing can change it afterwards."
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                value={statusNote}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => {
                  setStatusNote(e.target.value);
                  setStatusErrors({});
                }}
                placeholder="What was done, or what the ticket is waiting on"
              />
            )}
          </Field>
        </div>
      </Dialog>

      <Dialog
        open={performanceOpen}
        onClose={() => setPerformanceOpen(false)}
        title="Support performance"
        wide
        actions={<Button onClick={() => setPerformanceOpen(false)}>Close</Button>}
      >
        <div className="stack">
          {performance === null || performanceText === null ? (
            <div className="skeleton" style={{ height: '140px' }}>
              &nbsp;
            </div>
          ) : (
            <DataTable
              caption={performanceText.caption}
              columns={performanceColumns}
              rows={performance.items}
              rowKey={(a) => a.agent}
              empty={
                <EmptyState icon="chart-line" title={performanceText.emptyTitle}>
                  {performanceText.emptyBody}
                </EmptyState>
              }
            />
          )}

          {/* What these figures are, where the design asked for something else.
              The design describes "rolling 30 days, first reply excluding time
              paused on Pending player, CSAT from the post-resolution survey".
              GET /api/support/performance does none of the three: it counts every
              ticket assigned to the agent with no date window, its first reply is
              wall-clock first_reply_at − created_at (so time paused on Pending
              player is counted), and there is no CSAT table, so neither "SLA met %"
              nor "CSAT" can be drawn. The notice says each fact as an operator
              reads it. Its last sentence says whose tickets the rows count: every
              brand's, or only those of the reader's brands — never "every ticket
              ever assigned", which a restricted reader's rows are not, and which a
              handed-over ticket never was (it counts for whoever holds it now). */}
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              There is no date window: a ticket assigned to an agent counts however old it is.
              First reply is measured from when the ticket was opened, so time spent waiting on the
              player is included. There is no satisfaction figure, because player satisfaction is
              not surveyed, and no “SLA met” figure, because these figures do not compare a
              ticket&rsquo;s first reply with its target.
              {performanceText ? ` ${performanceText.scope}` : ''}
            </div>
          </div>
        </div>
      </Dialog>

      {/* ── New ticket, logged on a player's behalf ─────────────────────────
          Every control here was disabled, over a notice naming three missing
          things: POST /api/support/tickets, a `channel` column, and a player
          lookup. All three exist now — the route in
          services/api/src/modules/backoffice/support-intake.ts, the column in
          packages/db/sql/220_support_channel.sql, and the lookup is
          GET /api/players, the same search the Players module lists from.

          What is still NOT true is the old footnote's promise that logging a
          ticket "notifies them in-app so they can follow the thread". There is
          no player-facing support surface anywhere in this platform, so the
          player cannot read this thread and is not told it exists. The note at
          the foot says that instead of promising a notification with no sender
          behind it. */}
      <Dialog
        open={newTicketOpen}
        onClose={() => setNewTicketOpen(false)}
        title="New ticket"
        actions={
          <>
            <Button onClick={() => setNewTicketOpen(false)}>Close</Button>
            <Button variant="primary" busy={creating} onClick={() => void submitNewTicket()}>
              Create ticket
            </Button>
          </>
        }
      >
        <div className="stack">
          <Field
            label="Player"
            error={intakeErrors['playerId']}
            hint="Searches the player roster by reference, name, phone, email or agent code — inside your brand scope."
          >
            {({ id, describedBy, invalid }) => (
              <div className="stack">
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  aria-invalid={invalid}
                  value={intakeQuery}
                  onChange={(e) => {
                    setIntakeQuery(e.target.value);
                    setIntakeErrors({});
                  }}
                  placeholder="PL-10241, a name, 0917…"
                />
                <Select
                  aria-label="Matching players"
                  value={intakePlayer?.id ?? ''}
                  disabled={intakeCandidates.length === 0}
                  onChange={(e) => {
                    setIntakePlayer(
                      intakeCandidates.find((p) => p.id === e.target.value) ?? null,
                    );
                    setIntakeErrors({});
                  }}
                >
                  {/* The placeholder states which of the four states this is.
                      "No player matches" and "type more" are different facts,
                      and an operator on a call needs to know which. */}
                  <option value="">
                    {intakeQuery.trim().length < 2
                      ? 'Type at least two characters…'
                      : intakeSearching
                        ? 'Searching…'
                        : intakeCandidates.length === 0
                          ? `No player in your scope matches “${intakeQuery.trim()}”`
                          : 'Choose a player…'}
                  </option>
                  {intakeCandidates.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.ref} · {p.name} · {p.brandName}
                      {p.status === 'ACTIVE' ? '' : ` · ${p.status.toLowerCase()}`}
                    </option>
                  ))}
                </Select>
              </div>
            )}
          </Field>

          <Field label="Subject" error={intakeErrors['subject']}>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={intakeSubject}
                onChange={(e) => {
                  setIntakeSubject(e.target.value);
                  setIntakeErrors({});
                }}
                placeholder="Deposit not credited — GCash ₱8,000"
              />
            )}
          </Field>

          <Field
            label="What the player reported"
            error={intakeErrors['body']}
            hint="Recorded as the first message on the thread, authored by you on the player's behalf — not as words the player wrote."
          >
            {({ id, describedBy, invalid }) => (
              <textarea
                id={id}
                className="input"
                rows={4}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={intakeBody}
                onChange={(e) => {
                  setIntakeBody(e.target.value);
                  setIntakeErrors({});
                }}
                placeholder="Called at 14:20: GCash reference 9912 debited, no balance credited."
              />
            )}
          </Field>

          <Field
            label="Channel"
            error={intakeErrors['channel']}
            hint="One of four, and the platform accepts no others."
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={intakeChannel}
                onChange={(e) => setIntakeChannel(e.target.value)}
              >
                {SUPPORT_TICKET_CHANNELS.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field label="Priority" error={intakeErrors['priority']}>
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={intakePriority}
                onChange={(e) => setIntakePriority(e.target.value)}
              >
                <option value="NORMAL">Normal</option>
                <option value="HIGH">High</option>
                <option value="LOW">Low</option>
              </Select>
            )}
          </Field>

          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              The ticket opens against the PLAYER, not against you: the queue names them, the brand
              is read from their account inside your scope, and the SLA clock starts now with no
              first reply stamped — the message below is your record of the call, not an answer to
              it. Your name and theirs are both on it.{' '}
              <strong>The player will read what you write here.</strong> It is saved as part of the
              conversation rather than as an internal note, and their support screen shows them
              their own tickets and threads — so write it as something they can read, not as a note
              to the desk. Use <strong>Internal note</strong> on the ticket itself for anything that
              is only for staff. They are not emailed about it, so they will see it the next time
              they open the app; tell them on the call how you will come back to them.
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}

/**
 * The ticket ref carried in the address bar.
 *
 * Accepts both the prototype's `#/support/TK-20481` and this console's plain
 * `#TK-20481`, so links written against either form still open the ticket.
 */
function readTicketHash(): string | null {
  let raw: string;
  try {
    raw = decodeURIComponent(window.location.hash.replace(/^#/, ''));
  } catch {
    return null;
  }
  const segments = raw.split('/').filter((part) => part.length > 0);
  const segment = segments.length > 0 ? segments[segments.length - 1] : undefined;
  return segment && segment !== 'support' ? segment : null;
}
